import { useMemo, useState } from 'react';
import { Box, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Button, PageShell } from 'components/AppShell';
import { computeDiff, countChanges } from '../lib/diffEngine';
import CompareInputs from '../components/CompareInputs';
import DiffViewer, { type DiffViewMode } from '../components/DiffViewer';
import DiffStats from '../components/DiffStats';

const TextComparePage = () => {
  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [leftFile, setLeftFile] = useState<string | null>(null);
  const [rightFile, setRightFile] = useState<string | null>(null);

  const [compared, setCompared] = useState(false);
  const [mode, setMode] = useState<DiffViewMode>('split');
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);

  const tokens = useMemo(
    () => (compared ? computeDiff(left, right, { ignoreCase, ignoreWhitespace }) : []),
    [compared, left, right, ignoreCase, ignoreWhitespace]
  );
  const stats = useMemo(() => countChanges(tokens), [tokens]);

  const canCompare = left.trim().length > 0 && right.trim().length > 0;

  const actions = compared ? (
    <Button variant="secondary" onClick={() => setCompared(false)}>
      Изменить
    </Button>
  ) : (
    <Button variant="primary" onClick={() => setCompared(true)} disabled={!canCompare}>
      Сравнить
    </Button>
  );

  const toolbar = compared ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v: DiffViewMode | null) => v && setMode(v)}
        >
          <ToggleButton value="split">Рядом</ToggleButton>
          <ToggleButton value="unified">Единый</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={ignoreCase}
              onChange={(e) => setIgnoreCase(e.target.checked)}
            />
          }
          label="Игнорировать регистр"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={ignoreWhitespace}
              onChange={(e) => setIgnoreWhitespace(e.target.checked)}
            />
          }
          label="Игнорировать пробелы"
        />
      </Box>
      <DiffStats added={stats.added} removed={stats.removed} />
    </Box>
  ) : undefined;

  return (
    <PageShell
      title="Сравнение текстов"
      subtitle="Пословное сравнение двух текстов"
      actions={actions}
      toolbar={toolbar}
    >
      {compared ? (
        <DiffViewer tokens={tokens} mode={mode} />
      ) : (
        <CompareInputs
          left={left}
          right={right}
          leftFile={leftFile}
          rightFile={rightFile}
          onLeftChange={setLeft}
          onRightChange={setRight}
          onLeftFile={(text, name) => {
            setLeft(text);
            setLeftFile(name || null);
          }}
          onRightFile={(text, name) => {
            setRight(text);
            setRightFile(name || null);
          }}
        />
      )}
    </PageShell>
  );
};

export default TextComparePage;
